import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  FormDefinitionBuilder,
  type FormDefinitionBuilderValue,
} from '@/components/appointment-forms/FormDefinitionBuilder';
import { FormPageLayout } from '@/components/layouts/FormPageLayout';
import {
  defaultsFromTemplate,
  templateSchema,
  type SelectedJobTypeLink,
  type TemplateFormValues,
} from '@/lib/checklists/templateForm';
import type { FormItemType } from '@/types/appointmentForm';
import type { ChecklistItemType, ChecklistTemplate, ChecklistVisibility } from '@/types/settings';

/** Forms-engine types the legacy checklist template API has no field for. */
const LEGACY_CHECKLIST_UNSUPPORTED_TYPES: FormItemType[] = ['TIME', 'ADDRESS', 'FILE_UPLOAD'];

interface ChecklistOutlineBuilderPageProps {
  onClose: () => void;
  onBack?: () => void;
  editing: ChecklistTemplate | null;
  selectedJobTypeLinks: SelectedJobTypeLink[];
  onChangeJobTypeLinks: (links: SelectedJobTypeLink[]) => void;
  onSubmit: (values: TemplateFormValues) => void;
  isPending: boolean;
}

export function ChecklistOutlineBuilderPage({
  onClose,
  onBack,
  editing,
  selectedJobTypeLinks,
  onChangeJobTypeLinks,
  onSubmit,
  isPending,
}: ChecklistOutlineBuilderPageProps) {
  const [value, setValue] = useState<FormDefinitionBuilderValue>(() =>
    checklistToBuilderValue(editing, selectedJobTypeLinks),
  );
  const [issues, setIssues] = useState<string[]>([]);
  const totalItems = value.sections.reduce((total, section) => total + section.items.length, 0);

  function handleChange(next: FormDefinitionBuilderValue) {
    const jobTypesChanged = !sameIds(value.jobTypeIds, next.jobTypeIds);
    const blockingChanged = value.blocksCompletion !== next.blocksCompletion;

    if (jobTypesChanged || blockingChanged) {
      onChangeJobTypeLinks(
        next.jobTypeIds.map((jobTypeId, index) => {
          const current = selectedJobTypeLinks.find((link) => link.jobTypeId === jobTypeId);
          return {
            jobTypeId,
            blocksCompletion: blockingChanged
              ? next.blocksCompletion
              : (current?.blocksCompletion ?? next.blocksCompletion),
            sortOrder: current?.sortOrder ?? index,
          };
        }),
      );
    }

    setValue(next);
    if (issues.length) setIssues([]);
  }

  function save() {
    const result = templateSchema.safeParse(builderValueToChecklist(value));
    if (!result.success) {
      setIssues(
        result.error.issues.map((issue) => {
          const location = issue.path.length ? `${formatIssuePath(issue.path)}: ` : '';
          return `${location}${issue.message}`;
        }),
      );
      return;
    }
    setIssues([]);
    onSubmit(result.data);
  }

  const cancel = onBack ?? onClose;

  return (
    <FormPageLayout
      title={editing ? 'Edit checklist' : 'Create checklist'}
      subtitle={`${value.sections.length} ${
        value.sections.length === 1 ? 'section' : 'sections'
      } · ${totalItems} ${totalItems === 1 ? 'item' : 'items'}`}
      onBack={cancel}
      actions={
        <>
          <Button type="button" variant="outline" onClick={cancel}>
            Cancel
          </Button>
          <Button type="button" disabled={isPending} onClick={save}>
            {isPending ? 'Saving…' : editing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="mx-auto max-w-[1280px] space-y-3 px-3 py-4 sm:px-4">
        {issues.length ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <div className="flex items-center gap-2 font-medium">
              <AlertCircle className="h-4 w-4" />
              Fix {issues.length} {issues.length === 1 ? 'issue' : 'issues'} before saving
            </div>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-xs">
              {issues.slice(0, 6).map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <FormDefinitionBuilder
          value={value}
          onChange={handleChange}
          surface="CHECKLIST"
          excludeTypes={LEGACY_CHECKLIST_UNSUPPORTED_TYPES}
        />
      </div>
    </FormPageLayout>
  );
}

function checklistToBuilderValue(
  template: ChecklistTemplate | null,
  links: SelectedJobTypeLink[],
): FormDefinitionBuilderValue {
  const source = defaultsFromTemplate(template);
  return {
    name: source.name,
    description: source.description ?? '',
    defaultVisibility: source.defaultVisibility,
    jobTypeIds: links.map((link) => link.jobTypeId),
    bookableServiceIds: [],
    blocksCompletion: links.length > 0 && links.every((link) => link.blocksCompletion),
    allowMultipleSubmissions: false,
    sections: source.sections.map((section, sectionIndex) => ({
      id: section.id,
      name: section.name,
      sortOrder: sectionIndex,
      repeatable: section.isRepeatable,
      repeatOptions: (section.repeatOptions ?? []).map((option) => option.value),
      items: section.items.map((item, itemIndex) => ({
        id: item.id,
        placementKey: item.placementKey,
        questionId: item.questionId,
        questionVersionId: item.questionVersionId,
        label: item.label,
        type: checklistTypeToBuilderType(item.type),
        required: item.required ?? false,
        sortOrder: itemIndex,
        description: item.description ?? '',
        photoRequired: item.photoRequired ?? false,
        minPhotos: item.minPhotos ?? 1,
        options: (item.options ?? []).map((option) => option.value),
        config: {
          min: numberFromText(item.numberMin),
          max: numberFromText(item.numberMax),
          unit: item.unit || undefined,
          units: splitCsv(item.unitsCsv),
          options: (item.options ?? []).map((option) => option.value),
          signerRole: item.signerRole || undefined,
          maxLength: numberFromText(item.maxLength),
          allowPast: item.allowPast ?? true,
          allowFuture: item.allowFuture ?? true,
          format: item.barcodeFormat ?? 'ANY',
          maxPhotos: item.maxPhotos,
        },
      })),
    })),
  };
}

function builderValueToChecklist(value: FormDefinitionBuilderValue): TemplateFormValues {
  return {
    name: value.name,
    description: value.description,
    defaultVisibility: (value.defaultVisibility ?? 'INTERNAL') as ChecklistVisibility,
    sections: value.sections.map((section) => ({
      id: section.id,
      name: section.name,
      isRepeatable: section.repeatable ?? false,
      repeatOptions: (section.repeatOptions ?? []).map((option) => ({ value: option })),
      items: section.items.map((item) => ({
        id: item.id,
        placementKey: item.placementKey,
        questionId: item.questionId,
        questionVersionId: item.questionVersionId,
        label: item.label,
        type: builderTypeToChecklistType(item.type),
        required: item.required,
        description: item.description ?? '',
        photoRequired: item.photoRequired ?? false,
        minPhotos: item.minPhotos ?? 1,
        maxPhotos: item.config?.maxPhotos,
        numberMin: textFromNumber(item.config?.min),
        numberMax: textFromNumber(item.config?.max),
        unit: item.config?.unit ?? '',
        unitsCsv: (item.config?.units ?? []).join(', '),
        options: (item.config?.options ?? item.options ?? []).map((option) => ({ value: option })),
        signerRole: item.config?.signerRole ?? '',
        maxLength: textFromNumber(item.config?.maxLength),
        allowPast: item.config?.allowPast ?? true,
        allowFuture: item.config?.allowFuture ?? true,
        barcodeFormat: item.config?.format ?? 'ANY',
      })),
    })),
  };
}

function checklistTypeToBuilderType(type: ChecklistItemType): FormItemType {
  if (type === 'DROPDOWN') return 'SINGLE_SELECT';
  if (type === 'PHOTO') return 'IMAGE_UPLOAD';
  return type;
}

function builderTypeToChecklistType(type: FormItemType): ChecklistItemType {
  if (type === 'SINGLE_SELECT' || type === 'MULTI_SELECT') return 'DROPDOWN';
  if (type === 'IMAGE_UPLOAD') return 'PHOTO';
  // Never offered here (see LEGACY_CHECKLIST_UNSUPPORTED_TYPES); kept total for the type.
  if (type === 'TIME' || type === 'ADDRESS' || type === 'FILE_UPLOAD') return 'TEXT';
  return type;
}

function numberFromText(value: string | undefined) {
  if (!value?.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function textFromNumber(value: number | undefined) {
  return value === undefined ? '' : String(value);
}

function splitCsv(value: string | undefined) {
  return (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function sameIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

function formatIssuePath(path: Array<PropertyKey>) {
  return path
    .map((part) => (typeof part === 'number' ? part + 1 : String(part)))
    .join(' › ')
    .replace(/sections › (\d+)/, 'Section $1')
    .replace(/items › (\d+)/, 'item $1');
}
